import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import FormInput from "../../../components/input/FormInput";
import { base_url } from "../../../components/apiCall/webserviceurl";

/* ------------------------------------------------------------------
   KVTTP-C1 (JNU) — Peer Evaluation Ledger
   Wired to the live API:

     GET  /evaluation/peer-groups            -> eligible groups + own_group_id
     GET  /evaluation/group-details/:group_id -> project + dynamic questions
     GET  /evaluation/my-submissions          -> list of THIS evaluator's
                                                  submissions, each with an
                                                  evaluation_id + group_id
     GET  /evaluation/:evaluation_id          -> full detail (incl. answers)
                                                  for one submission
     POST /evaluation/submit-evaluation       -> submits ratings/text answers

   Because the question set (criteria + weights + labels) now comes
   entirely from /group-details, the old hardcoded per-type steps
   are replaced by ONE dynamic "Criteria Review" step driven by
   question.section === "criteria", and ONE dynamic step for
   question.section === "general" (which also carries the
   strengths/suggestions text questions, doubling as "Review & Submit").

   ALREADY-EVALUATED GROUPS:
     - already_evaluated groups from /peer-groups stay selectable
       (not disabled) so they can be reviewed, not just re-attempted.
     - On mount, GET /evaluation/my-submissions is called once to get
       the full list of this evaluator's past submissions. Each entry
       carries { evaluation_id, group_id, project_title, format_type,
       score, is_final, submitted_on, finalized_on }.
     - When a peer group is selected, we look up that group's
       group_id inside the my-submissions list to find its
       evaluation_id (NOT group_id) — that evaluation_id is what gets
       sent to GET /evaluation/:evaluation_id to fetch the full
       answer set for that submission.
     - If a submission is found, its answers (ratings + text) are
       pre-filled into `answers`, so Steps 2 and 3 render fully
       populated. Every input in those steps is disabled (view-only).
     - The final-step Submit button is replaced with a disabled
       "Already Submitted" indicator instead of allowing a duplicate
       POST.
     - If no submission is found for that group, the form behaves
       exactly as before (blank, editable, normal submit flow).

   FIX (validation bug):
     Step 0 could never validate because `evaluatorName` was declared
     but never actually set — the name TextField renders the read-only
     `name` from localStorage, while its `onChange={setEvaluatorName}`
     was commented out, and `restart()` also had its
     `setEvaluatorName(name)` line commented out. Since
     `evaluatorName` stayed "" forever, `validateStep(0)` — which
     requires `Boolean(evaluatorName.trim())` — always failed, so
     "Continue" on Step 0 could never proceed.

     Fix: initialize `evaluatorName` state directly from `name` (the
     field is disabled/read-only anyway, so there is no other way for
     the user to set it), and re-enable resetting it on restart() for
     consistency.

   NOTE: endpoint paths are assumed to be relative to whatever axios
   baseURL is already configured elsewhere in your app. Adjust the
   path strings below if your actual routes are mounted differently.
------------------------------------------------------------------- */

const SCALE = [1, 2, 3, 4, 5];

function authHeaders(token) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

function RatingScale({ value, onChange, name, disabled }) {
  return (
    <div className="rating">
      <div className="rating__track" role="radiogroup" aria-label={name}>
        <span className="rating__rail" aria-hidden="true" />
        {SCALE.map((n) => {
          const active = value === n;
          return (
            <button
              type="button"
              key={n}
              className={`rating__pip${active ? " rating__pip--active" : ""}`}
              onClick={() => !disabled && onChange(n)}
              aria-pressed={active}
              aria-label={`${n} of 5`}
              disabled={disabled}
            >
              {n}
            </button>
          );
        })}
      </div>
      <div className="rating__ends">
        <span>Needs Improvement</span>
        <span>Excellent</span>
      </div>
    </div>
  );
}

function CriterionCard({ crit, value, onChange, error, disabled }) {
  return (
    <div className={`criterion${error ? " criterion--error" : ""}`}>
      <div className="criterion__head">
        <h3>{crit.label}</h3>
        <span className="weight-badge">{crit.weight}%</span>
      </div>
      <p className="criterion__desc">{crit.desc}</p>
      <RatingScale
        value={value}
        onChange={onChange}
        name={crit.label}
        disabled={disabled}
      />
      {error && (
        <p className="field-error">Please give a rating before continuing.</p>
      )}
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  placeholder,
  area,
  error,
  hint,
  disabled,
}) {
  return (
    <div className={`field${error ? " field--error" : ""}`}>
      <label>
        {label} <span className="req">*</span>
      </label>
      {hint && <p className="field__hint">{hint}</p>}
      {area ? (
        <textarea
          rows={4}
          value={value || ""}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      ) : (
        <input
          type="text"
          value={value || ""}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      )}
      {error && <p className="field-error">This field is required.</p>}
    </div>
  );
}

export default function PeerEvaluationForm() {
  let token = localStorage.getItem("token");
  let name = localStorage.getItem("name");

  const [step, setStep] = useState(0);
  const [showErrors, setShowErrors] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // ---- Evaluator (Section 1) ----
  // FIX: initialize from `name` (localStorage) instead of "" — the field
  // is read-only/disabled in the UI, so this was the only way it could
  // ever get set, and leaving it blank permanently broke Step 0 validation.
  const [evaluatorName, setEvaluatorName] = useState(name || "");
  const [evaluatorGroup, setEvaluatorGroup] = useState(""); // own_group_id, from /peer-groups

  // ---- Peer groups list (Section 2) ----
  const [peerGroups, setPeerGroups] = useState([]);
  const [peerGroupsLoading, setPeerGroupsLoading] = useState(true);
  const [peerGroupsError, setPeerGroupsError] = useState("");
  const [evaluationsDone, setEvaluationsDone] = useState(0);
  const [evaluationLimit, setEvaluationLimit] = useState(0);
  const [limitReached, setLimitReached] = useState(false);

  const [peerGroupNumber, setPeerGroupNumber] = useState(""); // selected group_number (string)
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  // ---- Group details / dynamic question set ----
  const [groupDetails, setGroupDetails] = useState(null); // { group_id, project_title, format_type, questions }
  const [groupDetailsLoading, setGroupDetailsLoading] = useState(false);
  const [groupDetailsError, setGroupDetailsError] = useState("");

  // ---- My submissions (NEW) — list of this evaluator's past submissions,
  // used only to resolve group_id -> evaluation_id ----
  const [mySubmissions, setMySubmissions] = useState([]);
  const [mySubmissionsLoading, setMySubmissionsLoading] = useState(true);
  const [mySubmissionsError, setMySubmissionsError] = useState("");

  // ---- Existing evaluation detail (for the selected group) ----
  const [evaluationDetail, setEvaluationDetail] = useState(null); // full object from GET /evaluation/:evaluation_id
  const [evaluationDetailLoading, setEvaluationDetailLoading] = useState(false);
  const [evaluationDetailError, setEvaluationDetailError] = useState("");

  // True once we know this evaluator already has a submission for the
  // currently selected group -> renders Steps 2/3 read-only.
  const isReadOnly = Boolean(evaluationDetail);

  // ---- Answers, keyed by ques_no ----
  const [answers, setAnswers] = useState({}); // { [ques_no]: number | string }

  // ---- Submit ----
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const steps = [
    "Evaluator",
    "Peer Group",
    "Criteria Review",
    "Review & Submit",
  ];
  const lastStep = steps.length - 1; // 3

  const allQuestions = groupDetails?.questions || [];
  const criteriaQuestions = useMemo(
    () => allQuestions.filter((q) => q.section === "criteria"),
    [allQuestions],
  );
  const generalQuestions = useMemo(
    () => allQuestions.filter((q) => q.section === "general"),
    [allQuestions],
  );
  const ratingQuestions = useMemo(
    () => allQuestions.filter((q) => q.answer_type === "rating"),
    [allQuestions],
  );
  const totalWeight = ratingQuestions.reduce((s, q) => s + (q.weight || 0), 0);

  const weightedScore = useMemo(() => {
    // If we already have a finalized/previous score from the server for
    // this group, show that instead of recomputing from local answers.
    if (
      evaluationDetail?.score !== undefined &&
      evaluationDetail?.score !== null
    ) {
      return Math.round(Number(evaluationDetail.score));
    }
    if (!totalWeight) return 0;
    const earned = ratingQuestions.reduce((s, q) => {
      const v = Number(answers[q.ques_no]) || 0;
      return s + (v / 5) * (q.weight || 0);
    }, 0);
    return Math.round((earned / totalWeight) * 100);
  }, [answers, ratingQuestions, totalWeight, evaluationDetail]);

  const setAnswer = (quesNo, value) => {
    if (isReadOnly) return; // guard against edits once we know it's already submitted
    setAnswers((a) => ({ ...a, [quesNo]: value }));
  };

  // ---------------------------------------------------------------------
  // GET /evaluation/peer-groups
  // ---------------------------------------------------------------------
  const fetchPeerGroups = async () => {
    try {
      setPeerGroupsLoading(true);
      setPeerGroupsError("");
      const res = await axios.get(
        `${base_url}/evaluation/peer-groups`,
        authHeaders(token),
      );
      const data = res?.data?.data;
      if (data) {
        setEvaluatorGroup(
          data.own_group_id !== undefined && data.own_group_id !== null
            ? String(data.own_group_id)
            : "",
        );
        setEvaluationsDone(data.evaluations_done ?? 0);
        setEvaluationLimit(data.evaluation_limit ?? 0);
        setLimitReached(Boolean(data.limit_reached));
        setPeerGroups(Array.isArray(data.groups) ? data.groups : []);
      }
    } catch (e) {
      console.log(e, "ERROR coming from peer-groups");
      setPeerGroupsError("Could not load peer groups. Please try again.");
    } finally {
      setPeerGroupsLoading(false);
    }
  };

  // ---------------------------------------------------------------------
  // GET /evaluation/group-details/:group_id
  // ---------------------------------------------------------------------
  const fetchGroupDetails = async (groupId) => {
    if (!groupId) return;
    try {
      setGroupDetailsLoading(true);
      setGroupDetailsError("");
      const res = await axios.get(
        `${base_url}/evaluation/group-details/${groupId}`,
        authHeaders(token),
      );
      const data = res?.data?.data;
      if (data) {
        setGroupDetails(data);
      }
    } catch (e) {
      console.log(e, "ERROR coming from group-details");
      setGroupDetailsError(
        "Could not load this group's evaluation form. Please try again.",
      );
      setGroupDetails(null);
    } finally {
      setGroupDetailsLoading(false);
    }
  };

  // ---------------------------------------------------------------------
  // GET /evaluation/my-submissions  (NEW)
  // Fetched once on mount. Gives us this evaluator's full submission
  // history, each entry carrying its own evaluation_id + group_id, so we
  // can resolve "which evaluation_id belongs to this group_id" whenever a
  // peer group is selected.
  // ---------------------------------------------------------------------
  const fetchMySubmissions = async () => {
    try {
      setMySubmissionsLoading(true);
      setMySubmissionsError("");
      const res = await axios.get(
        `${base_url}/evaluation/my-submissions`,
        authHeaders(token),
      );
      const data = res?.data?.data;
      setMySubmissions(
        Array.isArray(data?.submissions) ? data.submissions : [],
      );
    } catch (e) {
      console.log(e, "ERROR coming from my-submissions");
      setMySubmissions([]);
      setMySubmissionsError("Could not load your submitted evaluations.");
    } finally {
      setMySubmissionsLoading(false);
    }
  };

  // ---------------------------------------------------------------------
  // GET /evaluation/:evaluation_id  -> existing evaluation detail
  // Called with the evaluation_id resolved from mySubmissions (NOT the
  // group_id). If a submission exists, its answers are pre-filled into
  // `answers` so Steps 2/3 render fully populated (read-only).
  // ---------------------------------------------------------------------
  const fetchEvaluationDetail = async (evaluationId) => {
    if (!evaluationId) return;
    try {
      setEvaluationDetailLoading(true);
      setEvaluationDetailError("");
      const res = await axios.get(
        `${base_url}/evaluation/${evaluationId}`,
        authHeaders(token),
      );
      const data = res?.data?.data;
      setEvaluationDetail(data || null);

      if (data?.answers?.length) {
        const prefilled = {};
        data.answers.forEach((a) => {
          prefilled[a.ques_no] =
            a.answer_type === "text" ? a.text_value || "" : a.rating_value;
        });
        setAnswers((prev) => ({ ...prev, ...prefilled }));
      }
    } catch (e) {
      console.log(e, "ERROR coming from evaluation-detail");
      setEvaluationDetail(null);
      setEvaluationDetailError(
        "Could not load your existing evaluation for this group.",
      );
    } finally {
      setEvaluationDetailLoading(false);
    }
  };
  // ---------------------------------------------------------------------
  // POST /evaluation/submit-evaluation
  // ---------------------------------------------------------------------
  const submitEvaluation = async () => {
    if (!selectedGroupId) return;
    const payloadAnswers = allQuestions.map((q) => {
      if (q.answer_type === "text") {
        return { ques_no: q.ques_no, text_value: answers[q.ques_no] || "" };
      }
      return {
        ques_no: q.ques_no,
        rating_value: Number(answers[q.ques_no]) || 0,
      };
    });

    try {
      setSubmitting(true);
      setSubmitError("");
      await axios.post(
        `${base_url}/evaluation/submit`,
        { group_id: selectedGroupId, answers: payloadAnswers },
        authHeaders(token),
      );
      setSubmitted(true);
      // Refresh the submissions list so this group now shows as evaluated
      // (with a resolvable evaluation_id) if the user starts another one.
      fetchMySubmissions();
    } catch (e) {
      console.log(e, "ERROR coming from submit-evaluation");
      setSubmitError("Could not submit your evaluation. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    fetchPeerGroups();
    fetchMySubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectPeerGroup = (group) => {
    // Only block picking a NEW group once the evaluation limit is hit.
    // Already-evaluated groups stay selectable so they can be reviewed.
    if (!group.already_evaluated && limitReached) return;

    setPeerGroupNumber(String(group.group_number));
    setSelectedGroupId(group.group_id);
    setAnswers({});
    setEvaluationDetail(null);
    setEvaluationDetailError("");
    fetchGroupDetails(group.group_id);

    // Resolve this group's evaluation_id (if any) from mySubmissions,
    // then fetch that specific evaluation's full detail.
    const existing = mySubmissions.find(
      (s) => String(s.group_id) === String(group.group_id),
    );
    if (existing?.evaluation_id) {
      fetchEvaluationDetail(existing.evaluation_id);
    }
  };

  function validateStep(i) {
    if (i === 0)
      return Boolean(evaluatorName.trim()) && Boolean(evaluatorGroup);
    if (i === 1) return Boolean(selectedGroupId) && Boolean(groupDetails);
    if (i === 2)
      return criteriaQuestions.every((q) => {
        const v = answers[q.ques_no];
        return q.answer_type === "text" ? String(v || "").trim() : Boolean(v);
      });
    if (i === 3)
      return generalQuestions.every((q) => {
        const v = answers[q.ques_no];
        return q.answer_type === "text" ? String(v || "").trim() : Boolean(v);
      });
    return true;
  }

  function goNext() {
    console.log("pressings >>>>>>>");
    // In read-only (already-evaluated) mode, let the evaluator browse
    // forward freely without the "please fill this in" validation gate.

    if (!isReadOnly && !validateStep(step)) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    setStep((s) => s + 1);
  }

  function goBack() {
    setShowErrors(false);
    setStep((s) => Math.max(s - 1, 0));
  }

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [step]);

  async function handleSubmit() {
    if (isReadOnly) return; // guard: never re-submit an already-evaluated group
    for (let i = 0; i <= lastStep; i++) {
      if (!validateStep(i)) {
        setShowErrors(true);
        setStep(i);
        return;
      }
    }
    await submitEvaluation();
  }

  function restart() {
    setSubmitted(false);
    setStep(0);
    setShowErrors(false);
    setEvaluatorName(name || "");
    setPeerGroupNumber("");
    setSelectedGroupId(null);
    setGroupDetails(null);
    setEvaluationDetail(null);
    setEvaluationDetailError("");
    setAnswers({});
    setSubmitError("");
    fetchPeerGroups();
    fetchMySubmissions();
  }

  return (
    <div className="kv-app" style={{ width: "100%", paddingBottom: "150px" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,500;0,600;0,700;1,500&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@500;600&display=swap');

        html, body {
          overflow-x: hidden;
        }

        .kv-app {
          --ink: #2e7d32;
          --ink-soft: #55606E;
          --paper: #F6F3EC;
          --paper-raised: #FCFAF5;
          --line: #DFD8C8;
          --amber: #C98A3E;
          --amber-deep: #9C6428;
          --sage: #4B6455;
          --rust: #9C3D34;
          font-family: 'Inter', sans-serif;
          background: #fff;
          color: var(--ink);
          min-height: 100%;
          width: 100%;
          max-width: 100vw;
          overflow-x: hidden;
          box-sizing: border-box;
          padding: clamp(16px, 4vw, 48px);
          display: flex;
          justify-content: center;
        }
        .kv-app *, .kv-app *::before, .kv-app *::after { box-sizing: border-box; }

        .kv-shell { width: 100%; max-width: 100%; }

        .kv-tag {
          font-family: "Inter", "Segoe UI", Arial, sans-serif;
          font-size: 11px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--amber-deep);
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 10px;
        }
        .kv-tag::after {
          content: "";
          flex: 1;
          height: 1px;
          background: var(--line);
        }

        .kv-header {
          margin-top: -30px;
          margin-left: -50px;
        }

        .kv-header h1 {
          font-family: "raboto", sans-serif;
          font-weight: 700;
          font-size: clamp(26px, 4vw, 34px);
          margin: 0 0 6px;
          line-height: 1.15;
        }
        .kv-header p {
          margin: 0 0 28px;
          font-style: italic;
          color: var(--ink-soft);
          font-size: 14.5px;
        }

        /* progress ledger */
        .kv-progress {
          display: flex;
          gap: 6px;
          margin-bottom: 10px;
        }
        .kv-progress__seg {
          flex: 1;
          height: 4px;
          background: var(--line);
          border-radius: 2px;
          position: relative;
          overflow: hidden;
        }
        .kv-progress__seg::after {
          content: "";
          position: absolute;
          inset: 0;
          background: var(--ink);
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.35s ease;
        }
        .kv-progress__seg--done::after,
        .kv-progress__seg--current::after { transform: scaleX(1); }
        .kv-progress__seg--current::after { background: var(--amber); }

        .kv-progress-label {
          font-family: "Inter", "Segoe UI", Arial, sans-serif;
          font-size: 11px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--amber-deep);
          margin-bottom: 10px;
        }

        .kv-stepline {
          display: flex;
          justify-content: space-between;
          font-family: "Inter", "Segoe UI", Arial, sans-serif;
          font-size: 10px;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--ink-soft);
          margin-bottom: 30px;
          gap: 4px;
        }
        .kv-stepline span {
          flex: 1;
          text-align: center;
        }
        .kv-stepline span:first-child { text-align: left; }
        .kv-stepline span:last-child { text-align: right; }
        .kv-stepline span.active { color: var(--ink); font-weight: 600; }
        .kv-stepline span.done { color: var(--sage); }

        @media (max-width: 560px) {
          .kv-stepline span { display: none; }
          .kv-stepline span.active { display: block; flex: 1; text-align: center; }
        }

        .kv-card {
          background: #fff;
          border: 1px solid var(--line);
          border-radius: 10px;
          padding: clamp(20px, 4vw, 36px);
          box-shadow: 0 1px 2px rgba(27, 42, 74, 0.04), 0 12px 28px -18px rgba(27, 42, 74, 0.25);
          max-width: 100%;
          overflow-wrap: break-word;
        }

        .kv-sec-title {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 4px;
          flex-wrap: wrap;
        }
        .kv-sec-title h2 {
          font-family: "Inter", "Segoe UI", Arial, sans-serif;
          font-size: 21px;
          font-weight: 600;
          margin: 0;
        }
        .kv-sec-num {
          font-family: "Inter", "Segoe UI", Arial, sans-serif;
          font-size: 11px;
          letter-spacing: 0.1em;
          color: var(--amber-deep);
          white-space: nowrap;
        }
        .kv-sec-sub {
          color: var(--ink-soft);
          font-size: 14px;
          margin: 6px 0 26px;
        }

        .note-block {
          background: #FBF3E5;
          border: 1px dashed #E1C08C;
          border-radius: 8px;
          padding: 14px 16px;
          font-size: 13.5px;
          color: #6E4A1F;
          line-height: 1.55;
          margin-bottom: 22px;
        }
        .note-block strong { color: var(--amber-deep); }

        .status-block {
          border-radius: 8px;
          padding: 14px 16px;
          font-size: 13.5px;
          line-height: 1.55;
          margin-bottom: 22px;
        }
        .status-block--info {
          background: #EEF3EE;
          color: #33513A;
          border: 1px dashed #9FBBA6;
        }
        .status-block--error {
          background: rgba(156,61,52,0.06);
          color: var(--rust);
          border: 1px dashed var(--rust);
        }

        .field { margin-bottom: 22px; }
        .field label {
          display: block;
          font-weight: 600;
          font-size: 14.5px;
          margin-bottom: 4px;
        }
        .field .req { color: var(--rust); }
        .field__hint { font-size: 12.5px; color: var(--ink-soft); margin: 0 0 8px; }
        .field input[type="text"], .field textarea {
          width: 100%;
          max-width: 100%;
          font-family: 'Inter', sans-serif;
          font-size: 14.5px;
          padding: 11px 13px;
          border: 1px solid var(--line);
          border-radius: 7px;
          background: #fff;
          color: var(--ink);
          resize: vertical;
          transition: border-color .15s ease, box-shadow .15s ease;
        }
        .field input[type="text"]:focus, .field textarea:focus {
          outline: none;
          border-color: var(--amber);
          box-shadow: 0 0 0 3px rgba(201,138,62,0.18);
        }
        .field input:disabled, .field textarea:disabled {
          background: #F6F3EC;
          color: var(--ink-soft);
          cursor: not-allowed;
        }
        .field--error input, .field--error textarea { border-color: var(--rust); }
        .field-error { color: var(--rust); font-size: 12.5px; margin: 6px 0 0; }

        .type-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin-bottom: 8px;
        }
        @media (max-width: 640px) { .type-grid { grid-template-columns: 1fr; } }

        .type-option {
          border: 1px solid var(--line);
          background: #fff;
          border-radius: 8px;
          padding: 14px 16px;
          cursor: pointer;
          text-align: left;
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          font-weight: 500;
          color: var(--ink);
          transition: border-color .15s ease, background .15s ease;
        }
        .type-option:hover { border-color: var(--amber); }
        .type-option--active {
          border-color: var(--ink);
          background: var(--ink);
          color: #fff;
        }
        .type-option--disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
        .type-option-sub {
          display: block;
          font-size: 11.5px;
          font-weight: 400;
          margin-top: 4px;
          opacity: 0.8;
        }

        /* peer-group number pill grid */
        .pill-grid {
          display: grid;
          grid-template-columns: repeat(var(--cols, 4), 1fr);
          gap: 10px;
          margin-bottom: 8px;
        }
        @media (max-width: 480px) {
          .pill-grid { grid-template-columns: repeat(4, 1fr); }
        }
        .pill {
          border: 1px solid var(--line);
          background: #fff;
          border-radius: 8px;
          padding: 12px 0;
          cursor: pointer;
          font-family: "Inter", "Segoe UI", Arial, sans-serif;
          font-size: 14.5px;
          font-weight: 600;
          color: var(--ink);
          transition: all .15s ease;
        }
        .pill:hover { border-color: var(--amber); color: var(--amber-deep); }
        .pill--active {
          border-color: var(--ink);
          background: var(--ink);
          color: #fff;
        }

        .criterion {
          border-top: 1px solid var(--line);
          padding: 22px 0;
        }
        .criterion:first-of-type { border-top: none; padding-top: 0; }
        .criterion--error { background: rgba(156,61,52,0.04); border-radius: 8px; padding: 18px 16px; margin: 0 -16px; }
        .criterion__head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
        }
        .criterion__head h3 {
          font-family: 'Lora', serif;
          font-size: 16.5px;
          font-weight: 600;
          margin: 0;
        }
        .weight-badge {
          font-family: "Inter", "Segoe UI", Arial, sans-serif;
          font-size: 11.5px;
          background: #EDE6D6;
          color: var(--amber-deep);
          padding: 3px 9px;
          border-radius: 20px;
          white-space: nowrap;
        }
        .criterion__desc {
          margin: 6px 0 16px;
          font-size: 13.5px;
          color: var(--ink-soft);
          line-height: 1.5;
        }

        .rating__track {
          position: relative;
          display: flex;
          justify-content: space-between;
          max-width: 320px;
        }
        .rating__rail {
          position: absolute;
          top: 50%;
          left: 18px;
          right: 18px;
          height: 1px;
          background: var(--line);
          transform: translateY(-50%);
          z-index: 0;
        }
        .rating__pip {
          position: relative;
          z-index: 1;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: 1.5px solid var(--line);
          background: #fff;
          font-family: "Inter", "Segoe UI", Arial, sans-serif;
          font-size: 13px;
          font-weight: 600;
          color: var(--ink-soft);
          cursor: pointer;
          transition: all .15s ease;
        }
        .rating__pip:hover { border-color: var(--amber); color: var(--amber-deep); }
        .rating__pip--active {
          background: var(--ink);
          border-color: var(--ink);
          color: #fff;
          box-shadow: 0 0 0 4px rgba(201,138,62,0.22);
        }
        .rating__pip:disabled {
          cursor: not-allowed;
          opacity: 0.85;
        }
        .rating__ends {
          display: flex;
          justify-content: space-between;
          max-width: 320px;
          margin-top: 8px;
          font-size: 11px;
          color: var(--ink-soft);
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        @media (max-width: 420px) {
          .rating__track, .rating__ends { max-width: 100%; }
          .rating__pip { width: 32px; height: 32px; }
        }

        .kv-nav {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 30px;
          gap: 12px;
        }
        .btn {
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 14px;
          padding: 11px 22px;
          border-radius: 7px;
          border: 1px solid var(--ink);
          cursor: pointer;
          transition: all .15s ease;
        }
        .btn-ghost { background: transparent; color: var(--ink); }
        .btn-ghost:hover { background: rgba(27,42,74,0.06); }
        .btn-ghost:disabled { opacity: 0.35; cursor: not-allowed; }
        .btn-solid { background: var(--ink); 
        // color: #fff;
         }
        .btn-solid:hover { background: var(--amber-deep); border-color: var(--amber-deep); }
        .btn-solid:disabled { opacity: 0.5; cursor: not-allowed; }

        /* review + submit */
        .review-list { list-style: none; margin: 0 0 22px; padding: 0; }
        .review-list li {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          padding: 10px 0;
          border-top: 1px solid var(--line);
          font-size: 13.5px;
        }
        .review-list li:first-child { border-top: none; }
        .review-list .rl-label { color: var(--ink-soft); }
        .review-list .rl-val { font-family: "Inter", "Segoe UI", Arial, sans-serif; font-weight: 600; }

        .score-panel {
          background: var(--ink);
          color: #fff;
          border-radius: 10px;
          padding: 22px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          margin-bottom: 24px;
          flex-wrap: wrap;
        }
        .score-panel .label {
          font-family: "Inter", "Segoe UI", Arial, sans-serif;
          font-size: 11px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #E2C89A;
        }
        .score-panel .num {
          font-family: 'Lora', serif;
          font-size: 40px;
          font-weight: 700;
        }

        .stamp-wrap {
          text-align: center;
          padding: 24px 0 4px;
        }
        .stamp {
          display: inline-block;
          border: 3px solid var(--sage);
          color: var(--sage);
          font-family: "Inter", "Segoe UI", Arial, sans-serif;
          font-weight: 600;
          letter-spacing: 0.14em;
          padding: 10px 22px;
          border-radius: 8px;
          transform: rotate(-3deg);
          margin-bottom: 18px;
        }
        .submitted-title {
          font-family: 'Lora', serif;
          font-size: 24px;
          font-weight: 700;
          margin: 0 0 8px;
        }
        .submitted-sub { color: var(--ink-soft); font-size: 14px; margin: 0 0 26px; }

        /* ---------------- Responsive ---------------- */

        @media (max-width: 900px) {
          .kv-header {
            margin-left: -20px;
          }
        }

        @media (max-width: 700px) {
          .kv-app {
            padding: clamp(14px, 4vw, 28px);
          }

          .kv-header {
            margin-top: -10px;
            margin-left: 0;
          }

          .kv-card {
            padding: clamp(16px, 4vw, 24px);
          }

          .kv-sec-title h2 {
            font-size: 18px;
          }

          .kv-sec-sub {
            font-size: 13px;
            margin-bottom: 20px;
          }

          .criterion {
            padding: 18px 0;
          }

          .criterion--error {
            margin: 0;
            padding: 16px;
          }

          .criterion__head h3 {
            font-size: 15px;
          }

          .weight-badge {
            font-size: 10.5px;
          }

          .score-panel {
            padding: 18px 18px;
          }

          .score-panel .num {
            font-size: 32px;
          }

          .kv-progress-label,
          .kv-tag {
            font-size: 10px;
          }
        }

        @media (max-width: 480px) {
          .kv-nav { flex-direction: column-reverse; align-items: stretch; }
          .kv-nav .btn { width: 100%; text-align: center; }

          .kv-header h1 {
            font-size: 22px;
          }

          .kv-card {
            padding: 16px;
            border-radius: 8px;
          }

          .kv-sec-title {
            gap: 6px;
          }

          .kv-sec-num {
            font-size: 10px;
          }

          .type-option {
            padding: 12px 14px;
            font-size: 13.5px;
          }

          .pill {
            padding: 10px 0;
            font-size: 13px;
          }

          .note-block {
            padding: 12px 14px;
            font-size: 12.5px;
          }

          .review-list li {
            font-size: 12.5px;
          }

          .stamp {
            padding: 8px 18px;
            letter-spacing: 0.1em;
          }

          .submitted-title {
            font-size: 20px;
          }
        }

        @media (max-width: 380px) {
          .kv-header h1 {
            font-size: 19px;
          }

          .kv-card {
            padding: 12px;
          }

          .rating__pip {
            width: 30px;
            height: 30px;
            font-size: 12px;
          }

          .score-panel {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          .score-panel .num {
            font-size: 28px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .kv-progress__seg::after {
            transition: none;
          }
        }
      `}</style>

      <div className="kv-shell">
        <div className="kv-header">
          <h1>Peer Evaluation Ledger</h1>
        </div>

        {!submitted && (
          <>
            <div className="kv-progress" style={{ marginTop: "20px" }}>
              {steps.map((_, i) => (
                <div
                  key={i}
                  className={`kv-progress__seg ${
                    i < step
                      ? "kv-progress__seg--done"
                      : i === step
                        ? "kv-progress__seg--current"
                        : ""
                  }`}
                />
              ))}
            </div>
            <div className="kv-progress-label">
              Step {step + 1} of {steps.length}
            </div>
            <div className="kv-stepline">
              {steps.map((s, i) => (
                <span
                  key={s}
                  className={i === step ? "active" : i < step ? "done" : ""}
                >
                  {s}
                </span>
              ))}
            </div>
          </>
        )}

        <div className="kv-card">
          {submitted ? (
            <div className="stamp-wrap">
              <div className="stamp">SUBMITTED</div>
              <h2 className="submitted-title">Evaluation recorded</h2>
              <p className="submitted-sub">
                Thanks, {evaluatorName || "evaluator"} — your review of "
                {groupDetails?.project_title || "this project"}" (Peer Group{" "}
                {peerGroupNumber || "—"}) has been logged.
              </p>
              <div className="score-panel">
                <span className="label">Weighted Score</span>
                <span className="num">{weightedScore}/100</span>
              </div>
              <ul className="review-list">
                {ratingQuestions.map((q) => (
                  <li key={q.ques_no}>
                    <span className="rl-label">{q.label}</span>
                    <span className="rl-val">
                      {answers[q.ques_no] || "—"}/5
                    </span>
                  </li>
                ))}
              </ul>
              <button className="btn btn-solid" onClick={restart}>
                Submit another evaluation
              </button>
            </div>
          ) : (
            <>
              {step === 0 && (
                <>
                  <div className="kv-sec-title">
                    <h2>Evaluator Details</h2>
                    <span className="kv-sec-num">SEC 01 / 04</span>
                  </div>
                  <p className="kv-sec-sub">
                    Tell us who's grading and which rotation you're in.
                  </p>
                  <div className="note-block">
                    <strong>Before you begin:</strong> evaluate the peer's
                    presentation and discussion as a whole — avoid scoring by
                    memory of index alone. Please follow the assigned order of
                    peer evaluations for your group.
                  </div>
                  <TextField
                    label="Your Name"
                    value={name}
                    disabled={true}
                    // onChange={setEvaluatorName}
                    // placeholder="e.g. Aditi Sharma — 2023CSB045"
                    // error={showErrors && !evaluatorName.trim()}
                  />
                  <div
                    className={`field${showErrors && !evaluatorGroup ? " field--error" : ""}`}
                  >
                    <label>
                      Evaluator Group Number{" "}(Your)
                      <span className="req">*</span>
                    </label>
                    <p className="field__hint">
                      Detected automatically from your account.
                    </p>
                    <div className="kvs-card">
                      <FormInput
                        id="formatType"
                        type="select"
                        required
                        placeholder={
                          peerGroupsLoading ? "Loading…" : "Your answer"
                        }
                        disabled
                        options={evaluatorGroup ? [evaluatorGroup] : []}
                        value={evaluatorGroup}
                        onChange={setEvaluatorGroup}
                        error={showErrors}
                      />
                    </div>
                    {showErrors && !evaluatorGroup && (
                      <p className="field-error">
                        Please select your evaluator group.
                      </p>
                    )}
                  </div>
                </>
              )}

              {step === 1 && (
                <>
                  <div className="kv-sec-title">
                    <h2>Evaluation of your Peer Group</h2>
                    <span className="kv-sec-num">SEC 02 / 04</span>
                  </div>
                  <p className="kv-sec-sub">
                    Which group's presentation are you evaluating right now?
                  </p>

                  {evaluationLimit > 0 && (
                    <div className="status-block status-block--info">
                      You've completed {evaluationsDone} of {evaluationLimit}{" "}
                      required evaluations.
                      {limitReached && " You've reached your evaluation limit."}
                    </div>
                  )}

                  {peerGroupsError && (
                    <div className="status-block status-block--error">
                      {peerGroupsError}
                    </div>
                  )}
                  {mySubmissionsError && (
                    <div className="status-block status-block--error">
                      {mySubmissionsError}
                    </div>
                  )}

                  <div
                    className={`field${showErrors && !selectedGroupId ? " field--error" : ""}`}
                  >
                    <label>
                      Peer Group Number <span className="req">*</span>
                    </label>
                    <div className="type-grid">
                      {peerGroupsLoading || mySubmissionsLoading ? (
                        <p className="field__hint">Loading peer groups…</p>
                      ) : (
                        peerGroups.map((g) => {
                          const isActive =
                            peerGroupNumber === String(g.group_number);
                          // Only block picking a NEW group once the limit is
                          // hit — an already-evaluated group stays clickable
                          // so it can be reviewed.
                          const notSubmitted = g.project_title === null && g.format_type === null; 
                          const disabled = !g.already_evaluated && limitReached || notSubmitted;
                          return (
                            <button
                              type="button"
                              key={g.group_id}
                              disabled={disabled}
                              className={`type-option${isActive ? " type-option--active" : ""}${disabled ? " type-option--disabled" : ""}`}
                              onClick={() => handleSelectPeerGroup(g)}
                            >
                              Group {g.group_id}
                              <span className="type-option-sub">
                                {g.already_evaluated
                                  ? "Already evaluated — tap to view"
                                  : g.project_title}
                              </span>
                              {notSubmitted && (
                                <span>
                                    TOP has not been submitted yet.
                                </span>
                              )}
                            </button>
                          );
                        })
                      )}
                    </div>
                    {showErrors && !selectedGroupId && (
                      <p className="field-error">
                        Please select the peer group number.
                      </p>
                    )}
                  </div>

                  {groupDetailsLoading && (
                    <p className="field__hint">Loading project details…</p>
                  )}
                  {groupDetailsError && (
                    <div className="status-block status-block--error">
                      {groupDetailsError}
                    </div>
                  )}

                  {groupDetails && (
                    <>
                      <TextField
                        label="Project Title"
                        value={groupDetails.project_title}
                        onChange={() => {}}
                        placeholder="Project title"
                        error={false}
                        disabled
                      />
                      <div className="field">
                        <label>Project Type</label>
                        <p className="field__hint">
                          {groupDetails.format_type == "design_synthesis"
                            ? "Design Synthesis"
                            : groupDetails.format_type ==
                                "algorithm_development"
                              ? "Algorithm Development"
                              : "Literature search"}
                        </p>
                      </div>
                    </>
                  )}

                  {evaluationDetailLoading && (
                    <p className="field__hint">
                      Loading your existing evaluation…
                    </p>
                  )}
                  {evaluationDetailError && (
                    <div className="status-block status-block--error">
                      {evaluationDetailError}
                    </div>
                  )}
                  {evaluationDetail && (
                    <div className="status-block status-block--info">
                      You already submitted an evaluation for this group
                      {evaluationDetail.is_final ? " (finalized)" : ""} on{" "}
                      {evaluationDetail.submitted_on
                        ? new Date(
                            evaluationDetail.submitted_on,
                          ).toLocaleDateString()
                        : "—"}{" "}
                      — score: {evaluationDetail.score}/100. Continue to view
                      your full submitted answers.
                    </div>
                  )}
                </>
              )}

              {step === 2 && (
                <>
                  <div className="kv-sec-title">
                    <h2>Criteria Review</h2>
                    <span className="kv-sec-num">SEC 03 / 04</span>
                  </div>
                  <p className="kv-sec-sub">
                    {isReadOnly
                      ? "You already submitted these ratings for this group."
                      : "Score each criterion, 1 (needs improvement) to 5 (excellent)."}
                  </p>
                  {isReadOnly && (
                    <div className="status-block status-block--info">
                      Viewing your previously submitted evaluation — read only.
                    </div>
                  )}
                  {criteriaQuestions.map((q) =>
                    q.answer_type === "text" ? (
                      <TextField
                        key={q.ques_no}
                        label={q.label}
                        area
                        value={answers[q.ques_no]}
                        onChange={(v) => setAnswer(q.ques_no, v)}
                        placeholder={q.description}
                        error={
                          showErrors &&
                          !isReadOnly &&
                          !String(answers[q.ques_no] || "").trim()
                        }
                        disabled={isReadOnly}
                      />
                    ) : (
                      <CriterionCard
                        key={q.ques_no}
                        crit={{
                          label: q.label,
                          weight: q.weight,
                          desc: q.description,
                        }}
                        value={answers[q.ques_no]}
                        onChange={(v) => setAnswer(q.ques_no, v)}
                        error={showErrors && !isReadOnly && !answers[q.ques_no]}
                        disabled={isReadOnly}
                      />
                    ),
                  )}
                </>
              )}

              {step === 3 && (
                <>
                  <div className="kv-sec-title">
                    <h2>General Evaluation</h2>
                    <span className="kv-sec-num">SEC 04 / 04</span>
                  </div>
                  {isReadOnly && (
                    <div className="status-block status-block--info">
                      Viewing your previously submitted evaluation — read only.
                    </div>
                  )}
                  {generalQuestions.map((q) =>
                    q.answer_type === "text" ? (
                      <TextField
                        key={q.ques_no}
                        label={q.label}
                        area
                        value={answers[q.ques_no]}
                        onChange={(v) => setAnswer(q.ques_no, v)}
                        placeholder={q.description}
                        error={
                          showErrors &&
                          !isReadOnly &&
                          !String(answers[q.ques_no] || "").trim()
                        }
                        disabled={isReadOnly}
                      />
                    ) : (
                      <CriterionCard
                        key={q.ques_no}
                        crit={{
                          label: q.label,
                          weight: q.weight,
                          desc: q.description,
                        }}
                        value={answers[q.ques_no]}
                        onChange={(v) => setAnswer(q.ques_no, v)}
                        error={showErrors && !isReadOnly && !answers[q.ques_no]}
                        disabled={isReadOnly}
                      />
                    ),
                  )}
                  {submitError && (
                    <div className="status-block status-block--error">
                      {submitError}
                    </div>
                  )}
                </>
              )}

              <div className="kv-nav">
                <button
                  className="btn btn-ghost"
                  onClick={goBack}
                  disabled={step === 0}
                >
                  ← Back
                </button>
                {step < lastStep ? (
                  <button className="btn btn-solid" onClick={goNext}>
                    Continue →
                  </button>
                ) : isReadOnly ? (
                  <button className="btn btn-ghost" disabled>
                    Already Submitted
                  </button>
                ) : (
                  <button
                    className="btn btn-solid"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? "Submitting…" : "Submit Evaluation"}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}